if (!customElements.get('mikan-pump-filters')) {
  customElements.define('mikan-pump-filters', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.form = this.querySelector('[data-pump-form]');
      this.selects = [...this.querySelectorAll('[data-pump-filter]')];
      this.message = this.querySelector('[data-pump-message]');
      this.labels = JSON.parse(this.querySelector('[data-pump-labels]').textContent);
      this.base = new URL(this.dataset.baseUrl, location.origin);
      this.panel = this.querySelector('[data-pump-panel]');
      this.summary = this.querySelector('[data-pump-summary]');
      this.mobile = matchMedia('(max-width: 749px)');
      this.mobile.addEventListener('change', () => { this.panel.open = !this.mobile.matches; }, options);
      this.syncFromUrl();
      this.form.addEventListener('change', () => this.updateOptions(), options);
      this.form.addEventListener('submit', event => {
        event.preventDefault();
        if (this.products && !this.matches(this.selected()).length) {
          this.updateOptions();
          return;
        }
        this.navigate(this.buildUrl(this.selected()));
      }, options);
      if (this.products) this.updateOptions();
      else this.loadIndex(this.controller.signal);
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
    }

    syncFromUrl() {
      const path = location.pathname.slice(this.base.pathname.replace(/\/$/, '').length);
      this.applied = path.replace(/^\//, '').split('+').filter(Boolean).map(tag => {
        try { return decodeURIComponent(tag); } catch { return tag; }
      });
      this.extras = this.applied.filter(tag => !this.selects.some(select => [...select.options].some(option => option.value === tag)));
      this.selects.forEach(select => {
        const applied = this.applied.find(tag => [...select.options].some(option => option.value === tag));
        select.value = applied || '';
      });
      if (this.selects.some(select => ['rating', 'voltage', 'series'].includes(select.dataset.pumpFilter) && select.value)) {
        this.querySelector('[data-pump-advanced]').open = true;
      }
      this.querySelector('[data-pump-clear]').href = this.buildUrl([], false).href;
      this.renderApplied();
      this.updateSummary();
      this.panel.open = !this.mobile.matches;
      this.updateOptions();
    }

    updateSummary() {
      const labels = this.selects.filter(select => select.value).map(select => {
        const label = select.selectedOptions[0].textContent.trim();
        return select.dataset.pumpFilter === 'model_power' ? `${label} HP / THP` : label;
      });
      this.summary.textContent = labels.length ? labels.join(' · ') : this.labels.filter;
    }

    navigate(url) {
      const event = new CustomEvent('mikan:filter-navigate', {
        bubbles: true,
        cancelable: true,
        detail: { url: url.href }
      });
      if (this.dispatchEvent(event)) {
        url.hash = 'ResultsList';
        location.assign(url.href);
      }
    }

    selected() {
      return [...new Set([...this.selects.map(select => select.value).filter(Boolean), ...this.extras])];
    }

    buildUrl(tags, keepFilters = true) {
      const url = new URL(this.base.href);
      url.pathname = url.pathname.replace(/\/$/, '') + (tags.length ? '/' + tags.map(encodeURIComponent).join('+') : '');
      new URL(location.href).searchParams.forEach((value, key) => {
        if (['preview_theme_id', '_fd', 'pb', 'sort_by'].includes(key) || (keepFilters && key.startsWith('filter.'))) {
          url.searchParams.append(key, value);
        }
      });
      if (this.dataset.previewTheme) url.searchParams.set('preview_theme_id', this.dataset.previewTheme);
      return url;
    }

    renderApplied() {
      const active = this.querySelector('[data-pump-active]');
      active.replaceChildren();
      this.applied.forEach(tag => {
        const option = this.selects.flatMap(select => [...select.options]).find(option => option.value === tag);
        const label = option?.textContent || tag.replace(/^mikan-pump-[^-]+-/, '').replaceAll('-', ' ');
        const link = document.createElement('a');
        link.textContent = this.labels.remove.replace('[value]', label) + ' ×';
        link.href = this.buildUrl(this.applied.filter(value => value !== tag)).href;
        active.append(link);
      });
    }

    matches(tags) {
      return this.products.filter(product => tags.every(tag => product.tags.includes(tag)));
    }

    updateOptions() {
      const button = this.form.querySelector('button[type="submit"]');
      const hasNativeFilters = [...new URL(location.href).searchParams.keys()].some(key => key.startsWith('filter.'));
      const selected = this.selected();
      const unchanged = selected.length === this.applied.length && selected.every(tag => this.applied.includes(tag));
      const count = this.products && !hasNativeFilters ? this.matches(selected).length : unchanged ? Number(this.dataset.resultCount) : null;
      button.textContent = count === null ? this.labels.view : count === 1 ? this.labels.one : this.labels.many.replace('[count]', count);
      if (!this.products) return;
      this.selects.forEach(select => {
        const otherTags = [...this.selects.filter(other => other !== select).map(other => other.value).filter(Boolean), ...this.extras];
        [...select.options].forEach(option => {
          option.disabled = Boolean(option.value && !option.selected && !this.matches([...otherTags, option.value]).length);
        });
      });
      const empty = this.matches(this.selected()).length === 0;
      this.message.textContent = this.labels.empty;
      this.message.hidden = !empty;
      this.form.querySelector('button[type="submit"]').disabled = empty;
    }

    async loadIndex(signal) {
      try {
        let url = this.buildUrl([], false);
        url.searchParams.delete('sort_by');
        url.searchParams.set('view', 'mikan-pump-index');
        const products = [];
        const visited = new Set();
        while (url && !visited.has(url.href)) {
          visited.add(url.href);
          const response = await fetch(url.href, { credentials: 'same-origin', signal });
          if (!response.ok) throw new Error('Pump index unavailable');
          const page = await response.json();
          if (!Array.isArray(page.products)) throw new Error('Invalid pump index');
          products.push(...page.products);
          if (!page.next) { url = null; continue; }
          const next = new URL(page.next, this.base);
          if (next.origin !== this.base.origin) throw new Error('Invalid index origin');
          next.searchParams.set('view', 'mikan-pump-index');
          if (this.dataset.previewTheme) next.searchParams.set('preview_theme_id', this.dataset.previewTheme);
          url = next;
        }
        if (url) throw new Error('Repeated index page');
        if (signal.aborted) return;
        this.products = products;
        this.updateOptions();
      } catch {
        if (signal.aborted) return;
        this.products = null;
      }
    }
  });
}


